import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Открытый тест IELTS — шаг истории: адрес `?test=<id>` (02.10.2026, свайп «назад» к сборке 1.1.1).
 *
 * ⚠️ ЗАЧЕМ. Reading, Listening и Writing держали открытый тест в `useState`: адрес не менялся, и «назад» — жест
 * от края на iPhone, кнопка на Android — уводил со страницы целиком, мимо списка. Теперь открытие — переход
 * (новая запись истории), а «Выйти» / «К заданиям» и системное «назад» — шаг назад на тот же экран со списком.
 *
 * ⚠️ АДРЕС С `?test=` САМ ТЕСТ НЕ ОТКРЫВАЕТ. Тест открыт, только если его открыли в ЭТОМ запуске приложения
 * (`opened`). Перезапуск, обновление страницы, ссылка с чужим или удалённым id — список, а параметр из адреса
 * снимается: пустого экрана «тест не найден» быть не должно, а ответы всё равно лежат в черновике на устройстве.
 */

const PARAM = 'test';

/** Что открывали в этом запуске. Живёт в памяти: после перезагрузки пусто. */
const opened = new Set<string>();

/**
 * Какой тест открыт. Чистая функция — ради проверки.
 * `validIds === undefined` — список ещё грузится: открытому в этом запуске верим (список уже был на экране).
 */
export function openTestId(
  param: string | null,
  openedIds: ReadonlySet<string>,
  validIds: readonly string[] | undefined,
): string | null {
  if (!param || !openedIds.has(param)) return null;
  if (validIds && !validIds.includes(param)) return null;
  return param;
}

/** Только для проверок: забыть открытое, как после перезапуска. */
export function __forgetOpenedTests(): void {
  opened.clear();
}

export function useOpenTest(validIds: readonly string[] | undefined): {
  id: string | null;
  open: (id: string) => void;
  close: () => void;
} {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const param = params.get(PARAM);
  const id = openTestId(param, opened, validIds);

  // Параметр есть, а тест не открыт (перезапуск, чужой или удалённый id) — снимаем его заменой адреса,
  // чтобы «назад» с такого экрана не возвращал на тот же адрес.
  useEffect(() => {
    if (param === null || id !== null) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(PARAM);
        return next;
      },
      { replace: true },
    );
  }, [param, id, setParams]);

  /**
   * ⚠️ ОДНО НАЖАТИЕ — ОДИН ШАГ ИСТОРИИ (находка на стенде 02.10.2026). Карточка теста и кнопка «Начать» внутри неё
   * обе зовут открытие: событие всплывает, и за одно нажатие приходило два вызова. Пока открытый тест жил в
   * `useState`, это было незаметно; с шагом истории в неё ложились ДВЕ записи, и «Выйти» с «назад» возвращали на
   * тот же тест вместо списка. Повторный вызов до смены адреса — не новый шаг. То же у выхода: два нажатия подряд
   * не должны шагнуть назад дважды.
   */
  const busy = useRef<string | null>(null);
  useEffect(() => {
    busy.current = null;
  }, [param]);

  const open = useCallback(
    (testId: string) => {
      opened.add(testId);
      if (param === testId || busy.current === `open:${testId}`) return;
      busy.current = `open:${testId}`;
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(PARAM, testId);
        return next;
      });
    },
    [param, setParams],
  );

  // Тест открыт только переходом из списка — значит, предыдущая запись истории и есть список.
  const close = useCallback(() => {
    if (param === null || busy.current === 'close') return;
    busy.current = 'close';
    navigate(-1);
  }, [param, navigate]);

  return { id, open, close };
}
